import { DEFAULT_MAX_ANGLE_DEG, SectionFrame, Vec3 } from './types'

/**
 * How a frame is stored, and how it is read back.
 *
 * SHARED WITH THE CUSTOMIZER. This is the wire format, so the two repos agreeing on it is
 * not a nicety — a section written by the admin portal and read by the Customizer has to
 * land on the same millimetres of the same surface.
 *
 * It lives in `sections.target_object`, which is a `jsonb` column (migration 57) already
 * carrying shape_type, radius, vertices, material_type and pixel_to_size. So this needs
 * NO schema migration, and a row without a `placement` key keeps reading exactly as it
 * does today.
 *
 * Note what is deliberately NOT here: width and height. Those already exist as
 * `default_size.width/height` on the section itself and mean the same thing under either
 * model — millimetres across the engraved area. Duplicating them into the placement would
 * create two sources of truth that could disagree after an edit.
 */
export interface SectionPlacement {
  /** Centre of the section in the product root's local space, metres. */
  origin: Vec3
  /** Unit vector the section faces along. */
  normal: Vec3
  /** Unit vector for the section's +V, giving its in-plane rotation. */
  up: Vec3
  /** Projection throw in millimetres. */
  depth: number
  /** Curvature the artwork wraps around, millimetres. 0 is a flat decal. */
  wrapRadius: number
  /** Surface angle limit in degrees — see SectionFrame.maxAngleDeg. */
  maxAngleDeg: number
}

/** A section as it reaches this code from either repo's data layer. */
export interface SectionLike {
  /** Live value from the admin form, before it is folded into target_object on save. */
  placement?: unknown
  sectionId?: string | null
  id?: string | null
  section_id?: string | null
  name?: string | null
  section_name?: string | null
  width?: number | string | null
  height?: number | string | null
  targetObject?: { name?: string | null; placement?: unknown } | null
  target_object?: { name?: string | null; placement?: unknown } | null
}

function num(v: unknown, fallback: number): number {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''))
  return Number.isFinite(n) ? n : fallback
}

function vec3(v: unknown, fallback: Vec3): Vec3 {
  if (!Array.isArray(v) || v.length < 3) return fallback
  const out: Vec3 = [num(v[0], fallback[0]), num(v[1], fallback[1]), num(v[2], fallback[2])]
  return out
}

/** Read a stored placement, tolerating partial or malformed JSON rather than throwing. */
export function parsePlacement(raw: unknown): SectionPlacement | null {
  if (!raw || typeof raw !== 'object') return null
  const p = raw as Record<string, unknown>
  if (!Array.isArray(p.origin) || !Array.isArray(p.normal)) return null

  return {
    origin: vec3(p.origin, [0, 0, 0]),
    normal: vec3(p.normal, [0, 0, 1]),
    up: vec3(p.up, [0, 1, 0]),
    depth: num(p.depth, 30),
    wrapRadius: num(p.wrapRadius, 0),
    maxAngleDeg: num(p.maxAngleDeg, DEFAULT_MAX_ANGLE_DEG),
  }
}

/**
 * Turn one stored section into a renderable frame, or null when it has no placement.
 *
 * Accepts both naming conventions on purpose: the admin portal's form data uses
 * `targetObject` / `sectionId`, the Customizer's API data uses `target_object` /
 * `section_id`, and V2 form values arrive as strings. One reader for both beats two that
 * drift.
 */
export function frameFromSection(section: SectionLike | null | undefined): SectionFrame | null {
  if (!section) return null

  const target = section.targetObject ?? section.target_object
  const meshName = target?.name
  if (!meshName) return null

  // Both shapes, and the live one wins.
  //
  // Stored, a placement lives inside `target_object` — that is the jsonb column. But while
  // an operator is editing, the admin form holds it as its own top-level value and only
  // folds it into target_object on save. Reading only the stored location meant a section
  // drew nothing until it had been saved and reloaded, which looks exactly like the
  // feature not working.
  const placement = parsePlacement((section as { placement?: unknown }).placement ?? target?.placement)
  if (!placement) return null

  const width = num(section.width, 0)
  const height = num(section.height, 0)
  if (width <= 0 || height <= 0) return null

  return {
    id: String(section.sectionId ?? section.section_id ?? section.id ?? meshName),
    name: String(section.name ?? section.section_name ?? 'Section'),
    meshName: String(meshName),
    width,
    height,
    ...placement,
  }
}

/** Every section of a profile that has a placement, in list order. */
export function framesFromSections(sections: ReadonlyArray<SectionLike | null | undefined>): SectionFrame[] {
  return sections.map(frameFromSection).filter((f): f is SectionFrame => f !== null)
}

/** The half of a frame that gets stored. Width and height stay on the section. */
export function placementFromFrame(frame: SectionFrame): SectionPlacement {
  return {
    origin: frame.origin,
    normal: frame.normal,
    up: frame.up,
    depth: frame.depth,
    wrapRadius: frame.wrapRadius,
    maxAngleDeg: frame.maxAngleDeg,
  }
}
